/**
 * FERP Portal - Context Banner
 *
 * DRR-188 H3: Role-aware urgency surface showing aggregated pending items
 * per navigation group. Uses constellation-driven labels (e.g., "3 residents
 * need care plan review" for ALF, "3 clients need attention" for home care).
 *
 * Appears below FocusBanner when there are pending items. Click a badge to
 * navigate directly to the relevant page.
 *
 * The banner is controlled by the backend:
 * - `contextBanner.enabled` from setting YAML enables/disables
 * - `contextBanner.alwaysVisible` controls dashboard-only vs all-pages
 * - `contextBanner.maxGroups` limits how many groups appear
 * - `navigationSummary` query returns pre-rendered action labels
 *
 * Usage:
 * Place in the portal layout below FocusBanner:
 * ```tsx
 * <FocusBanner />
 * <ContextBanner />
 * <Outlet />
 * ```
 */

import { Group, Text, Badge, Paper, Loader } from '@mantine/core';
import { IconBellRinging } from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useViewLayer } from '@/auth/AuthContext';
import {
  useGetSettingNavigationQuery,
  useGetNavigationSummaryQuery,
  ViewLayer as GqlViewLayer,
} from '@/generated/graphql';

// ============================================================================
// VIEW LAYER MAPPING
// ============================================================================

const VIEW_LAYER_TO_GQL: Record<string, GqlViewLayer> = {
  user: GqlViewLayer.USER,
  admin: GqlViewLayer.ADMIN,
  architect: GqlViewLayer.ARCHITECT,
};

// ============================================================================
// COMPONENT
// ============================================================================

export function ContextBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const viewLayer = useViewLayer();

  // Get context banner config from setting
  const { data: settingData } = useGetSettingNavigationQuery();
  const bannerConfig = settingData?.settingBundle?.contextBanner;

  // Skip if banner disabled or config not loaded
  const enabled = bannerConfig?.enabled ?? false;
  const alwaysVisible = bannerConfig?.alwaysVisible ?? false;
  const maxGroups = bannerConfig?.maxGroups ?? 3;
  const pollInterval = (bannerConfig?.pollIntervalSeconds ?? 60) * 1000;

  // Only show on dashboard unless alwaysVisible
  const isDashboard = location.pathname === '/' || location.pathname === '/dashboard';
  const shouldShow = enabled && (alwaysVisible || isDashboard);

  const gqlLayer = VIEW_LAYER_TO_GQL[viewLayer] ?? GqlViewLayer.USER;

  // Fetch navigation summary (pending counts per group)
  const { data: summaryData, loading } = useGetNavigationSummaryQuery({
    variables: { layer: gqlLayer },
    skip: !shouldShow,
    pollInterval: shouldShow ? pollInterval : 0,
    fetchPolicy: 'cache-and-network',
  });

  if (!shouldShow) return null;

  const groups = (summaryData?.navigationSummary ?? [])
    .filter((g) => g.pendingCount > 0)
    .sort((a, b) => {
      // Urgent items float to top, then by sort order
      if (a.urgentCount > 0 && b.urgentCount === 0) return -1;
      if (b.urgentCount > 0 && a.urgentCount === 0) return 1;
      return a.sortOrder - b.sortOrder;
    })
    .slice(0, maxGroups);

  // Nothing pending — don't render
  if (!loading && groups.length === 0) return null;

  return (
    <Paper
      p="xs"
      radius={0}
      data-testid="context-banner"
      style={{
        borderBottom: '2px solid var(--mantine-color-orange-4)',
        backgroundColor: 'var(--mantine-color-orange-0)',
      }}
    >
      <Group gap="sm" justify="center" wrap="wrap">
        <IconBellRinging size={16} color="var(--mantine-color-orange-6)" />

        {loading && groups.length === 0 && (
          <Group gap="xs">
            <Loader size="xs" />
            <Text size="sm" c="dimmed">Loading...</Text>
          </Group>
        )}

        {groups.map((group) => (
          <Badge
            key={group.groupId}
            variant={group.urgentCount > 0 ? 'filled' : 'light'}
            color={group.urgentCount > 0 ? 'red' : 'orange'}
            size="lg"
            style={{ cursor: 'pointer' }}
            data-testid={`context-banner-${group.groupId}`}
            onClick={() => navigate(group.primaryPath)}
          >
            {group.actionLabel}
          </Badge>
        ))}
      </Group>
    </Paper>
  );
}
